import { Injectable, inject } from '@angular/core';
import { AbstractControl, AsyncValidator, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Structure } from '@app/core/models/structure.model';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { StructureService } from '../structure/structure.service';
import {
    alphaNumeric,
    alphanumericMajuscule,
    integerOnly,
    numberUpToFourDigitsAndTwoDecimals,
    numberWithTwoDigits,
    yearAsInteger
} from './patterns';

export const odfPatterValidator = (config: { pattern: RegExp; message: string }): ValidatorFn => {
    return (control: AbstractControl) => {
        if (control.value && !control.value.toString().match(config.pattern)) {
            return { pattern: config.message };
        }
        return null;
    };
};

const codeCertificationPatterns: Record<string, { pattern: RegExp; message: string }> = {
    RNCP: { pattern: /^RNCP\d{5}$/, message: 'Format attendu: RNCP suivi de 5 chiffres' },
    RS: { pattern: /^RS\d{4}$/, message: 'Format attendu: RS suivi de 4 chiffres' }
};

/** Le code de certification est obligatoire et son format dépend du type de certification choisi. */
export function codeCertificationValidator(control: AbstractControl): ValidationErrors | null {
    const type = control.parent?.get('typeCertification')?.value;
    const config = type ? codeCertificationPatterns[type] : undefined;
    if (!config) {
        return null;
    }
    if (!control.value) {
        return { required: true };
    }
    return config.pattern.test(control.value) ? null : { pattern: config.message };
}

export const descripteursObjetMaquetteValidators = {
    code: [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(30),
        odfPatterValidator({
            pattern: alphaNumeric,
            message: 'Le code doit contenir uniquement des caractères alphanumériques et/ou le tiret'
        })
    ],
    typeObjetMaquette: [Validators.required],
    mutualise: [Validators.required],

    libelle: [Validators.required, Validators.maxLength(50), Validators.minLength(3)],
    libelleLong: [Validators.required, Validators.maxLength(150), Validators.minLength(3)],

    description: [Validators.maxLength(2000)],
    ects: [odfPatterValidator({ pattern: numberUpToFourDigitsAndTwoDecimals, message: 'Le format est incorrect: format attendu XXXX.XX' })],
    structurePrincipale: [],
    stage: [Validators.required],
    nature: [],
    teleEnseignement: [Validators.required],
    typeObjetFormation: [Validators.required],
    natureObjetFormation: [],
    capaciteAccueil: [
        odfPatterValidator({ pattern: integerOnly, message: 'Nombre entier uniquement' }),
        Validators.minLength(0),
        Validators.maxLength(4)
    ],

    // FORMATION
    typeFormation: [Validators.required],

    // GROUPEMENT
    plageDeChoix: [],
    natureGroupement: [],
    plageDeChoixMin: [odfPatterValidator({ pattern: integerOnly, message: 'Nombres entiers uniquement' })],
    plageDeChoixMax: [odfPatterValidator({ pattern: integerOnly, message: 'Nombres entiers uniquement' })]
};

export const descripteursContexteValidators = {
    ects: [odfPatterValidator({ pattern: numberUpToFourDigitsAndTwoDecimals, message: 'Le format est incorrect: format attendu XXXX.XX' })],
    natureObjetFormation: [],
    natureGroupement: [],
    plageDeChoixMin: [odfPatterValidator({ pattern: integerOnly, message: 'Nombres entiers uniquement' })],
    plageDeChoixMax: [odfPatterValidator({ pattern: integerOnly, message: 'Nombres entiers uniquement' })]
};

export const descripteursEspaceValidators = {
    code: [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(15),
        odfPatterValidator({
            pattern: alphanumericMajuscule,
            message: 'Le code doit contenir uniquement des caractères alphanumériques et/ou le tiret'
        })
    ],
    libelle: [Validators.required, Validators.minLength(4), Validators.maxLength(50)],
    libelleLong: [Validators.required, Validators.minLength(4), Validators.maxLength(255)],
    libelleAffichage: [Validators.required, Validators.minLength(4), Validators.maxLength(150)],
    active: [Validators.required],
    anneeUniversitaire: [
        Validators.required,
        odfPatterValidator({ pattern: yearAsInteger, message: "L'année universitaire doit être un nombre à 4 chiffres ex: 2020" }),
        Validators.minLength(4),
        Validators.maxLength(4)
    ],

    typeEspace: [Validators.required],

    validite: [Validators.required],
    dateDebut: [Validators.required],
    dateFin: [Validators.required],

    dateConsommationNomenclatures: [Validators.required]
};

export const descripteursDiplomeCertificatAutre = {
    code: [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(25),
        odfPatterValidator({
            pattern: alphanumericMajuscule,
            message: 'Le code doit contenir uniquement des caractères alphanumériques et/ou le tiret'
        })
    ],
    finaliteDiplome: [Validators.required],
    libelleLong: [Validators.minLength(1), Validators.maxLength(150)],
    libelleCourt: [Validators.required, Validators.minLength(3), Validators.maxLength(50)],
    libelleAffichage: [Validators.minLength(1), Validators.maxLength(150)],
    intitule: [Validators.required, Validators.minLength(3), Validators.maxLength(500)],
    actif: [Validators.required],
    diplomeIntermediaire: [Validators.required],
    datArrete: [],
    dateDebut: [],
    dateFin: [],
    gradeDiplome: [],
    nomDiplome: [Validators.required, Validators.maxLength(255)],
    coAccredites: [Validators.maxLength(4)],
    etablissementDelivrantLeDiplome: [Validators.required],
    typeCertification: [],
    codeCertification: [codeCertificationValidator]
};

export const descripteursSyllabusValidators = {
    autresInformations: [Validators.maxLength(2000)],
    bibliographie: [Validators.maxLength(2000)],
    coefficient: [Validators.maxLength(200)],
    contacts: [Validators.maxLength(2000)],
    descripteursObjetFormationSyllabus: [],
    langueEnseignement: [Validators.maxLength(2000)],
    modalitesEnseignements: [Validators.maxLength(500)],
    modalitesEvaluation: [Validators.maxLength(2000)],
    objectif: [Validators.maxLength(2000)],
    description: [Validators.maxLength(2000)],
    ouvertureALaMobiliteEntrante: [],
    prerequisPedagogique: [Validators.maxLength(2000)],
    volumeHoraireParTypeDeCours: [Validators.maxLength(2000)]
};

export const descripteursFormatsEnseignementValidators = {
    structuresPorteuse: [],
    formationPorteuse: [],
    formatsEnseignement: [],

    modalite: [],
    typeHeure: [Validators.required],
    volumeHoraire: [
        Validators.required,
        ctrl => {
            if (ctrl.value) {
                if (!Number.isInteger(+ctrl.value?.heures) || !Number.isInteger(+ctrl.value?.minutes)) {
                    return { pattern: 'Le format attendu est du type HH MM' };
                }
                if (ctrl.value?.heures < 0 || ctrl.value.minutes < 0) {
                    return { pattern: 'Le volume horaire ne peut pas être négatif' };
                }
                return null;
            }
            return null;
        }
    ],
    nombreTheoriqueDeGroupes: [
        Validators.required,
        Validators.maxLength(2),
        odfPatterValidator({ pattern: integerOnly, message: 'Le nombre de groupes doit être un entier sur 2 caractères' })
    ],
    seuilDedoublement: [
        Validators.maxLength(3),
        odfPatterValidator({ pattern: integerOnly, message: 'Le seuil de dédoublement doit être un entier sur 3 caractères' })
    ]
};

export const descripteursEnqueteValidators = {
    descripteursSise: {
        typeDiplome: [],
        codeDiplomeSise: [],
        niveauDiplomeSise: [odfPatterValidator({ pattern: integerOnly, message: 'Le format est incorrect' }), Validators.maxLength(2)],
        mention: [],
        champFormation: [],
        niveauDiplome: [],
        parcoursTypeSise: []
    },
    descripteursAglae: {
        habilitePourBoursesAglae: [],
        niveauAglae: [odfPatterValidator({ pattern: numberWithTwoDigits, message: 'Le niveau Aglaé est incorrect' })]
    },
    descripteursFresq: {
        numeroFresqNiveau1: [odfPatterValidator({ pattern: /^\d{7}[A-Za-z]$/, message: 'Format incorrect' })],
        numeroFresqNiveau2: [odfPatterValidator({ pattern: /^\d{7}-\d{3}[A-Za-z]$/, message: 'Format incorrect' })]
    }
};

@Injectable({ providedIn: 'root' })
export class StructureValidator implements AsyncValidator {
    private service = inject(StructureService);

    validate(control: AbstractControl): Observable<ValidationErrors | null> {
        if (control.value) {
            return this.service.lireStructureByCode(control.value).pipe(
                map(() => null),
                catchError(() => of({ structureInexistante: "La structure n'existe pas." }))
            );
        }
        return of(null);
    }

    validateWithKey(key: keyof Structure & string, shouldBeValidAtDate: Date | null) {
        return (control: AbstractControl) => {
            if (control.value) {
                return this.service.lireStructureWithKey(control.value, key).pipe(
                    map((structure: Structure) =>
                        !!shouldBeValidAtDate && !structure.isValidAtDate(shouldBeValidAtDate)
                            ? { structureInexistante: "La structure n'est pas valide à cette date." }
                            : null
                    ),
                    catchError(() => of({ structureInexistante: "La structure n'existe pas." }))
                );
            }
            return of(null);
        };
    }
}
